import React, { Fragment, useEffect, useState } from "react";
import { ReactComponent as SearchIcon } from "assets/images/common/svgs/search.svg";
import { ReactComponent as DeleteIcon } from "assets/images/common/svgs/deleteIconTable.svg";
import { ReactComponent as EyeIcon } from "assets/images/common/svgs/eyeIconTable.svg";
import { siteRoutes } from "utils/helpers/enums/routes.enum";
import { useNavigate } from "react-router-dom";
import { confirmationPopup } from "utils/helpers/common/alert-service";
import Pagination from "components/particles/table/pagination";
import DataNotFound from "components/particles/table/data-not-found";
import { useSelector } from "react-redux";

import { BadgeDTO } from "utils/helpers/models/badge/badge.dto";
import {
  BadgeFiltersDTO,
  UserFiltersDTO,
} from "utils/helpers/models/common/user-filters.dto";
import editProfilePicUser from "assets/images/common/others/editProfileUSer.png";
import { BADGE_TYPE, ROLES } from "utils/helpers/enums/roles.enum";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import useBadges from "containers/badge-management/useHooks";
import { EditIcon } from "assets/images/common/svgs";
import useStore from "hooks/useStore";
import { sitePermissions } from "utils/helpers/enums/permissions.enum";
import { useDebounce } from "hooks/useDebounce";

const CoreConceptBadgeListing: React.FC = () => {
  const { hasPermission } = useStore();
  const navigate = useNavigate();
  const [data, setData] = useState<BadgeDTO[]>([]);
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const { getBadges, toggleStatus } = useBadges();
  const [totalElements, setTotalElements] = useState<number>(0);
  const [filters, setFilters] = useState<BadgeFiltersDTO>(
    new BadgeFiltersDTO()
  );
  const buildUpdatedFilters = (
    updates: Partial<BadgeFiltersDTO> = {},
    resetPage: boolean = false
  ) => {
    const updatedFilters = { ...filters, ...updates };
    if (resetPage) {
      updatedFilters.page = 1;
    }
    return updatedFilters;
  };

  const debounceSearch = useDebounce(filters.name, 1500);

  const onPageChange = (pageInfo: { selected: number; limit: number }) => {
    const { selected: page, limit } = pageInfo;
    const updatedFilters = buildUpdatedFilters(
      { limit: limit, page: page + 1 },
      false
    );
    setFilters(updatedFilters);
    getAllBadges(updatedFilters);
  };

  const getAllBadges = (filters: BadgeFiltersDTO) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== "") {
        filtersToSend[key] = filters[key];
      }
    }
    const queryParams = {
      ...filtersToSend,
      page: filters.page - 1,
      badgeType: BADGE_TYPE.CORE_CONCEPT,
    };
    getBadges(setData, queryParams, setFilters, setTotalElements);
  };

  useEffect(() => {
    getAllBadges(filters);
  }, [debounceSearch]);

  // Initial load on component mount
  useEffect(() => {
    getAllBadges(filters);
  }, []);

  const handleChangeFilter = (event: any) => {
    const {
      target: { name, value },
    } = event;
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const updatedFilters = buildUpdatedFilters(
      { [name]: trimmedValue },
      true
    );
    setFilters(updatedFilters);
    if (name === "name" && trimmedValue === "") {
      getAllBadges(updatedFilters);
    }
  };

  const handleSearch = (event: any) => {
    const { key } = event;
    if (key === "Enter") {
      const updatedFilters = buildUpdatedFilters({}, true);
      setFilters(updatedFilters);
      getAllBadges(updatedFilters);
    }
  };



  const handleChangeStatus = async (badge: any) => {
    const { id: badgeId, active } = badge;

    const action = active ? "Disable" : "Enable";

    const result = await confirmationPopup(
      `Are you sure you want to ${action} this Badge?`
    );

    if (result.isConfirmed) {
      await toggleStatus(badgeId);
      getAllBadges(filters);
    }
  };

  return (
    <div>
      <h1 className="tex-2xl sm:text-[32px] text-primaryText font-bold mb-6">
        Core Concept Badges
      </h1>
      <div className="flex justify-between items-center gap-5 mb-5">
        {hasPermission(sitePermissions.coreConceptBadgeCreate) && (
          <button
            onClick={() => {
              navigate(siteRoutes.createCoreConceptBadge);
            }}
            className="px-4 py-2 text-sm bg-goldenOrangeBg text-white rounded-lg font-semibold"
          >
            Add Badge
          </button>
        )}

        <div className="w-full sm:w-[388px] relative">
          <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-primaryText text-lg" />
          <input
            type="search"
            placeholder="Search by Badge Title"
            value={filters.name}
            onChange={handleChangeFilter}
            onKeyUp={handleSearch}
            name="name"
            className="w-full search-input pl-10 p-2 border-[0.6px] border-borderLightGray rounded-full focus:outline-none bg-coolLightGrayBg"
          />
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg">
        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.map((item: any, index: number) => (
            <div
              key={index + 1}
              className="bg-dividerLightBg relative border border-stokeLight rounded-[30px] p-6 flex flex-col items-center justify-center gap-2 text-center h-[322px]"
            >
              {/* <span
                                onClick={() => {
                                    navigate(`${siteRoutes.cupOfGratitudeEdit}?id=${item.id}`);
                                }}
                                className="bg-goldenOrangeBg cursor-pointer p-2 rounded-md absolute top-5 right-5"
                            >
                                <EditIcon />
                            </span> */}
              <div className="relative">
                <img
                  src={
                    item?.image
                      ? concatPathWithBackendUrl(item?.image?.name)
                      : ""
                  }
                  alt={item?.name}
                  className="w-[157px] h-[177px]"
                />
              </div>

              <h3 className="text-primaryText text-2xl font-bold">
                {item?.name || "-"}
              </h3>

              <span>
                {hasPermission(sitePermissions.coreConceptBadgeUpdate) ? (
                  <label className="switch-field">
                    <input
                      type="checkbox"
                      checked={item?.active}
                      onChange={() => handleChangeStatus(item)}
                    />
                    <span className="slider"></span>
                  </label>
                ) : (
                  <label className="switch-field cursor-not-allowed opacity-60">
                    <input type="checkbox" checked={item?.active} disabled />
                    <span className="slider slider-disabled"></span>
                  </label>
                )}
              </span>
            </div>
          ))}
        </div>
        <div>
          <DataNotFound show={!isLoading && !data.length} />
        </div>
      </div>
      <Fragment>
        {totalElements > 0 && (
          <Pagination
            count={totalElements}
            page={filters.page}
            limit={filters.limit}
            onPageChange={onPageChange}
          />
        )}
      </Fragment>
    </div>
  );
};

export default CoreConceptBadgeListing;
